/**
 * `useQueuePosition` — the visitor's live slot in the unclaimed queue.
 *
 * Polls the public queue endpoint every 10s while the chat is waiting for an
 * agent; returns null once claimed/closed (the `session.claimed` WS event flips
 * `active` off immediately, so the banner drops without waiting for a poll).
 */

import { useEffect, useState } from 'react';
import { getWidgetApi } from '../config/loadWidgetConfig';

const POLL_MS = 10_000;

export interface QueueSlot {
  position: number;
  estimatedWaitSeconds: number;
}

export function useQueuePosition(
  cid: string,
  sessionId: string | null,
  active: boolean,
): QueueSlot | null {
  const [slot, setSlot] = useState<QueueSlot | null>(null);

  useEffect(() => {
    if (!active || !cid || !sessionId) {
      setSlot(null);
      return;
    }
    let cancelled = false;
    const load = () => {
      getWidgetApi()
        .widget.queue(cid, sessionId)
        .then((res) => {
          if (cancelled) return;
          setSlot(
            res.waiting && res.position != null
              ? { position: res.position, estimatedWaitSeconds: res.estimatedWaitSeconds ?? 120 }
              : null,
          );
        })
        .catch(() => {
          /* transient — the next poll retries; the banner just stays as-is */
        });
    };
    load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [cid, sessionId, active]);

  return slot;
}

/** "~2 min" / "under a minute" — friendly wait phrasing. */
export function formatWait(seconds: number): string {
  if (seconds < 60) return 'under a minute';
  return `~${Math.round(seconds / 60)} min`;
}
